// @ts-check
// Páginas da seção Plataforma do painel de suporte: códigos de acesso, cupons do site e envio de e-mails (as seções
// que já existiam, agora cada uma na sua página), backups com o backup manual e a auditoria da plataforma com busca.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link } from '../../../app/router.js';
import { fetchAdminAudit, fetchAdminBackups, runAdminBackups } from '../../../data/operations.js';
import { formatDateTime } from '../../../lib/format.js';
import { adminInstitutionPath } from '../../../lib/admin-navigation.js';
import { auditActionLabel } from '../../../lib/audit-labels.js';
import { Button } from '../../../ui/button.js';
import { Tag } from '../../../ui/chip.js';
import { Icon } from '../../../ui/icon.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { formatByteSize, showOperationsError } from '../operations-format.js';
import { SignUpCodesSection } from '../sign-up-codes-section.js';
import { SiteCouponsSection } from '../site-coupons-section.js';
import { EmailSendingSection } from '../email-sending-section.js';
import { AdminEmpty, AdminLoading, AdminPage, AdminSection } from './admin-ui.js';
import { t } from '../../../i18n/index.js';

/** @param {{ runs: any[] }} props */
export function BackupTable({ runs }) {
  if (!runs || runs.length === 0) return html`<p class="admin-muted">${t('operations.admin.empty')}</p>`;
  return html`<div class="settings-table admin-table">
    <div class="settings-table-row settings-table-header admin-backup-row">
      <span>${t('operations.admin.backupColumns.startedAt')}</span><span>${t('operations.admin.backupColumns.kind')}</span>
      <span>${t('operations.admin.backupColumns.status')}</span><span class="settings-table-number">${t('operations.admin.backupColumns.rows')}</span>
      <span class="settings-table-number">${t('operations.admin.backupColumns.size')}</span>
    </div>
    ${runs.map((run) => html`<div key=${run.id} class="settings-table-row admin-backup-row" title=${run.errorMessage ?? run.objectKey ?? ''}>
      <span class="settings-table-muted">${formatDateTime(run.startedAt)}</span>
      <span>${t(`operations.admin.backupKinds.${run.kind}`)}</span>
      <span><${Tag} color=${run.status === 'SUCCEEDED' ? 'green' : 'red'} text=${t(`operations.admin.backupStatuses.${run.status}`)} /></span>
      <span class="settings-table-number">${String(run.rowCount)}</span>
      <span class="settings-table-number">${formatByteSize(run.byteSize)}</span>
    </div>`)}
  </div>`;
}

export function AdminBackupsPage() {
  const [runs, setRuns] = useState(/** @type {any[] | null} */ (null));
  const [isBackingUp, setIsBackingUp] = useState(false);
  const load = () => fetchAdminBackups().then(setRuns).catch(showOperationsError);
  useEffect(() => {
    load();
  }, []);
  const runBackups = async () => {
    setIsBackingUp(true);
    try {
      await runAdminBackups();
      showSuccessSnackbar(t('operations.admin.backupsDone'));
      load();
    } catch (error) {
      showOperationsError(error);
    } finally {
      setIsBackingUp(false);
    }
  };
  return html`<${AdminPage} icon="IconDatabase" title=${t('operations.admin.panel.sections.backups')} subtitle=${t('operations.admin.panel.backups.subtitle')}
    actions=${html`<${Button} title=${t('operations.admin.runBackups')} icon="IconDatabase" variant="secondary" size="small" isLoading=${isBackingUp} onClick=${runBackups} />`}>
    ${runs === null ? html`<${AdminLoading} rows=${4} />` : html`<${BackupTable} runs=${runs} />`}
  </${AdminPage}>`;
}

export function AdminAuditPage() {
  const [entries, setEntries] = useState(/** @type {any[] | null} */ (null));
  const [query, setQuery] = useState('');
  useEffect(() => {
    fetchAdminAudit().then(setEntries).catch(showOperationsError);
  }, []);
  const normalized = query.trim().toLowerCase();
  const visible = (entries ?? []).filter((entry) => !normalized || auditActionLabel(entry).toLowerCase().includes(normalized) || entry.action.includes(normalized) || entry.actorEmail.toLowerCase().includes(normalized));
  return html`<${AdminPage} icon="IconHistory" title=${t('operations.admin.panel.sections.audit')} subtitle=${t('operations.admin.panel.audit.subtitle')} wide=${true}>
    <div class="admin-toolbar">
      <label class="admin-search-field">
        <${Icon} name="IconSearch" size=${14} />
        <input class="admin-search-input" type="search" value=${query} placeholder=${t('operations.admin.panel.audit.search')} aria-label=${t('operations.admin.panel.audit.search')}
          onInput=${(/** @type {Event} */ event) => setQuery(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
    </div>
    ${entries === null ? html`<${AdminLoading} rows=${5} />` : visible.length === 0 ? html`<${AdminEmpty} icon="IconHistory" title=${t('operations.admin.empty')} />`
      : html`<div class="settings-table admin-table">
          <div class="settings-table-row settings-table-header admin-audit-row">
            <span>${t('operations.admin.panel.audit.when')}</span><span>${t('operations.admin.panel.audit.action')}</span><span>${t('operations.admin.panel.audit.actor')}</span><span>${t('operations.admin.panel.audit.institution')}</span>
          </div>
          ${visible.map((entry) => html`<div key=${entry.id} class="settings-table-row admin-audit-row" title=${JSON.stringify(entry.details ?? {})}>
            <span class="settings-table-muted">${formatDateTime(entry.createdAt)}</span>
            <span class="admin-cell-stack"><span>${auditActionLabel(entry)}</span><span class="settings-table-muted settings-table-mono">${entry.action}</span></span>
            <span class="settings-table-muted admin-cell-ellipsis">${entry.actorEmail}</span>
            <span>${entry.tenantId ? html`<${Link} href=${adminInstitutionPath(entry.tenantId)} className="admin-inline-link">${t('operations.admin.panel.audit.openInstitution')}</${Link}>` : html`<span class="settings-table-muted">-</span>`}</span>
          </div>`)}
        </div>`}
  </${AdminPage}>`;
}

export function AdminAccessCodesPage() {
  return html`<${AdminPage} icon="IconKey" title=${t('operations.admin.panel.sections.access-codes')} wide=${true}>
    <${AdminSection} title=${t('operations.admin.signUpCodes.title')} className="admin-section-plain"><${SignUpCodesSection} /></${AdminSection}>
  </${AdminPage}>`;
}

export function AdminCouponsPage() {
  return html`<${AdminPage} icon="IconTicket" title=${t('operations.admin.panel.sections.coupons')} wide=${true}>
    <${AdminSection} title=${t('operations.admin.siteCoupons.title')} className="admin-section-plain"><${SiteCouponsSection} /></${AdminSection}>
  </${AdminPage}>`;
}

export function AdminEmailSendingPage() {
  return html`<${AdminPage} icon="IconMail" title=${t('operations.admin.panel.sections.email-sending')} wide=${true}>
    <${AdminSection} title=${t('operations.emailSending.title')} className="admin-section-plain"><${EmailSendingSection} /></${AdminSection}>
  </${AdminPage}>`;
}
